import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useAuth } from 'react-oidc-context';

import { useClient } from 'urql';

import { config } from '../../../config/config';
import { GetActivityDocument } from '../queries/activity.graphql';
import type { IGetActivityQuery, IGetActivityQueryVariables } from '../queries/activity.graphql';
import type { IActivity } from '../reducers/activity-feed.reducer';
import { activityFeedReducer, INITIAL_STATE } from '../reducers/activity-feed.reducer';
import { EventCode, EventStatus } from '../util/events.constants';

/*
 * TODO: Majority of this file will be moved to backend side once #230 is completed.
 */

type EventNode = NonNullable<IGetActivityQuery['allUserActivities']>['edges'][number]['node'];

function getActivityStatus(code: string | null | undefined) {
    switch (code) {
        case EventCode.USER_LOGIN_ERROR:
        case EventCode.USER_CHANGE_PASSWORD_ERROR:
            return EventStatus.FAILED;
        case EventCode.USER_LOGIN:
        case EventCode.USER_DEPOSIT_SUCCESSFUL:
        case EventCode.USER_WITHDRAWAL_SUCCESSFUL:
            return EventStatus.SUCCESSFUL;
    }
    return EventStatus.NOT_APPLICABLE;
}

function mapToActivity(event: EventNode): IActivity | undefined {
    if (!event) {
        return;
    }

    return {
        id: event.id,
        activity: event.feedType ?? '',
        metadata: {
            ...event,
            ...(event.payload ?? {}),
        },
        status: getActivityStatus(event.feedType),
        time: event.lastUpdatedOn,
    };
}

export function useActivtyFeed(filters?: IGetActivityQueryVariables) {
    const client = useClient();
    const loadCount = useRef(0);
    const auth = useAuth();
    const userId = auth.user?.profile?.user_profile_id as string;

    const [state, dispatch] = useReducer(activityFeedReducer, INITIAL_STATE);

    const fetchMore = useCallback(
        async (currentState: typeof state) => {
            if (currentState.isLoading || !userId) {
                return;
            }
            loadCount.current++;
            const currentCount = loadCount.current;

            dispatch({ type: 'loading' });

            const conditions = {
                tenantId: config.TENANT_ID,
                userProfileId: userId,
            };

            const { data } = await client
                .query<IGetActivityQuery>(GetActivityDocument, {
                    first: 10,
                    after: currentState.cursor || null,
                    orderBy: filters?.orderBy || null,
                    conditions,
                    filters: filters?.filters,
                })
                .toPromise();

            if (loadCount.current !== currentCount) {
                // Abort loading if we've already triggered another load.
                return;
            }

            const events = data?.allUserActivities;
            const { endCursor, hasNextPage } = events?.pageInfo ?? {
                hasNextPage: false,
                endCursor: null,
            };
            const feed =
                events?.edges
                    .map((edge) => mapToActivity(edge.node))
                    .filter((item): item is NonNullable<typeof item> => !!item) ?? [];

            dispatch({
                type: 'complete',
                payload: {
                    feed,
                    hasMore: hasNextPage,
                    cursor: endCursor,
                },
            });
            dispatch({ type: 'loaded' });
        },
        [client, userId, filters],
    );

    useEffect(() => {
        dispatch({ type: 'reset' });
        fetchMore(INITIAL_STATE);
    }, [userId, fetchMore]);

    return useMemo(
        () => ({
            fetching: state.isLoading,
            fetchMore: () => fetchMore(state),
            feed: state.feed,
            hasMore: state.hasMore,
        }),
        [state, fetchMore],
    );
}
